import type { MoneyWire } from "@utopia-well-done-ltd/contracts";
import { ZodError } from "zod";

let locale = "en-GB";

export function setFormatLanguage(language: "en" | "fr") {
  locale = language === "fr" ? "fr-FR" : "en-GB";
}

/**
 * The panel's formatters, in one place.
 *
 * They were copied into five screens before this file existed, and the copies had already drifted:
 * one of them dropped the sign handling and rendered a negative amount as `-2.-50`. A formatter
 * that appears once cannot disagree with itself.
 */

/** A timestamp an operator reads. The raw ISO string is for machines, not for a table cell. */
export const asMoment = (iso: string): string =>
  new Date(iso).toLocaleString(locale, {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    timeZone: "UTC",
    year: "numeric",
  });

/** A calendar day, where the time of day would be noise. */
export const asDay = (iso: string): string =>
  new Date(iso).toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
    year: "numeric",
  });

/**
 * Minor units to a readable figure, without ever putting the value through a float.
 *
 * `BigInt` divide and remainder rather than `Number(amount) / 100`: the division is exact at every
 * size, and money is the one thing on screen where a rounded figure is a wrong figure.
 *
 * The sign comes off the value before the split. `-250n % 100n` is `-50n`, which renders as
 * `-2.-50` if the remainder is formatted directly — so the magnitude is formatted and the sign is
 * put back afterwards.
 *
 * Two decimal places assumes a currency with a 100-to-1 minor unit, which every currency this
 * platform takes today has. A three-decimal currency (KWD, BHD) would need the exponent from the
 * currency itself — this reads it from nowhere, so it is right for ours and wrong for those.
 */
export function asAmount(
  { amount, currency }: MoneyWire,
  options: { sign?: boolean } = {},
): string {
  const value = BigInt(amount);
  const negative = value < 0n;
  const absolute = negative ? -value : value;
  const major = absolute / 100n;
  const minor = (absolute % 100n).toString().padStart(2, "0");

  // A ledger line is a movement and reads better with its direction on it; a balance or a requested
  // amount is a quantity, and a leading `+` on one looks like a change rather than a figure.
  const prefix = negative ? "−" : options.sign ? "+" : "";
  return `${prefix}${major}.${minor} ${currency}`;
}

/** Show the operator-friendly seconds and the exact certified millisecond value together. */
export function asDuration(milliseconds: number): string {
  return `${milliseconds / 1000}s (${milliseconds} ms)`;
}

export function amountToMinor(amount: string): string {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(amount.trim());
  if (!match) throw new Error("Enter XTS amounts with no more than two decimal places.");
  return (BigInt(match[1] ?? "0") * 100n + BigInt((match[2] ?? "").padEnd(2, "0"))).toString();
}

export function minorToAmount(amount: string): string {
  const minor = BigInt(amount);
  return `${minor / 100n}.${(minor % 100n).toString().padStart(2, "0")}`;
}

export function asFormError(caught: unknown): string {
  if (caught instanceof ZodError) {
    return [...new Set(caught.issues.map(({ message }) => message))].join(" ");
  }
  return caught instanceof Error ? caught.message : "That request did not go through.";
}
